// Resources.js
import React, { useState, useEffect } from 'react';
import {
  FiFilter,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiTag,
  FiMapPin,
  FiBookOpen,
  FiArrowRight,
  FiSearch,
  FiX,
  FiCalendar,
  FiUser,
  FiGlobe,
} from 'react-icons/fi';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './Resources.css';
import Sidebar from '../Sidebar';
import { API_BASE_URL, toastdisplay } from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import { decodeHashId, encodeHashId } from '../../utils/hashids';
import { sanitizeHtml } from '../../utils/safeHtml';
// Import mock resources data
import mockResourcesData from '../../assets/mock-data/resources.json';

const Resources = () => {
  const navigate = useNavigate();
  const { resourceId } = useParams();
  const location = useLocation();
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [availableGeographies, setAvailableGeographies] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedGeographies, setSelectedGeographies] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [user] = useState(() => getStoredUser());
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en'); // Default to English

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if user is logged in - redirect to login if not
  useEffect(() => {
    if (!user) {
      // Store the intended URL for redirect after login
      const intendedUrl = resourceId
        ? `/resources/${resourceId}`
        : '/resources';
      sessionStorage.setItem('redirect_after_login', intendedUrl);

      // Redirect to login
      navigate('/login', {
        state: {
          from: location.pathname,
          message: 'Please log in or create an account to access resources',
        },
      });
    } else if (resourceId && user) {
      // User is logged in, fetch and display the specific resource
      fetchSpecificResource(resourceId);
    }
  }, [resourceId, user, navigate, location]);

  const fetchSpecificResource = async (hashId) => {
    try {
      // Decode the hashId to get the actual resource ID
      const actualResourceId = decodeHashId(hashId);

      // Use mock data for now
      const resource = mockResourcesData.find((r) => r.id === actualResourceId);

      if (!resource) {
        throw new Error('Resource not found');
      }

      setSelectedResource(resource);

      // TODO: Replace with API call when backend is ready
      // const response = await fetch(
      //   `${API_BASE_URL}/api/resources/${actualResourceId}`
      // );
      // if (!response.ok) {
      //   throw new Error('Resource not found');
      // }
      // const data = await response.json();
      // setSelectedResource(data.resource);
    } catch (error) {
      console.error('Error fetching specific resource:', error);
      toastdisplay(
        'Resource not found or you do not have access',
        false,
        3000,
        setToast
      );
      navigate('/resources');
    }
  };

  useEffect(() => {
    fetchResources();
  }, [selectedLanguage]); // Re-fetch when language changes

  const fetchResources = async () => {
    try {
      setIsLoadingResources(true);

      // Use mock data for now and filter by selected language
      const resourcesData = mockResourcesData.filter(
        (r) => r.language === selectedLanguage
      );
      setResources(resourcesData);
      setFilteredResources(resourcesData);

      // Extract unique values for filters
      const programs = [
        ...new Set(resourcesData.flatMap((r) => r.programs || [])),
      ];
      const schools = [
        ...new Set(resourcesData.flatMap((r) => r.schools || [])),
      ];
      const geographies = [
        ...new Set(resourcesData.flatMap((r) => r.geographies || [])),
      ];
      const tags = [...new Set(resourcesData.flatMap((r) => r.tags || []))];

      setAvailablePrograms(programs);
      setAvailableSchools(schools);
      setAvailableGeographies(geographies);
      setAvailableTags(tags);

      // TODO: Replace with API call when backend is ready
      // Check if we have cached data that's still valid (5 minutes)
      // const cachedData = localStorage.getItem('cachedResources');
      // const cacheTimestamp = localStorage.getItem('cachedResourcesTimestamp');
      // if (cachedData && cacheTimestamp) {
      //   const now = Date.now();
      //   const cacheAge = now - parseInt(cacheTimestamp);
      //   const fiveMinutes = 5 * 60 * 1000;
      //   if (cacheAge < fiveMinutes) {
      //     const parsedData = JSON.parse(cachedData);
      //     setResources(parsedData.resources);
      //     setFilteredResources(parsedData.resources);
      //     setAvailablePrograms(parsedData.programs);
      //     setAvailableSchools(parsedData.schools);
      //     setAvailableGeographies(parsedData.geographies);
      //     setAvailableTags(parsedData.tags);
      //     return;
      //   }
      // }
      // const res = await fetch(`${API_BASE_URL}/api/resources`);
      // if (!res.ok) throw new Error('Failed to load resources');
      // const data = await res.json();
      // const resourcesData = data.resources || [];
    } catch (err) {
      console.error('Error fetching resources:', err);
      toastdisplay('Failed to load resources', false, 3000, setToast);
    } finally {
      setIsLoadingResources(false);
    }
  };

  // Filter resources whenever filter selections change
  useEffect(() => {
    let filtered = resources;

    // Filter by programs
    if (selectedPrograms.length > 0) {
      filtered = filtered.filter((resource) =>
        resource.programs?.some((program) => selectedPrograms.includes(program))
      );
    }

    // Filter by schools
    if (selectedSchools.length > 0) {
      filtered = filtered.filter((resource) =>
        resource.schools?.some((school) => selectedSchools.includes(school))
      );
    }

    // Filter by geographies
    if (selectedGeographies.length > 0) {
      filtered = filtered.filter((resource) =>
        resource.geographies?.some((geo) => selectedGeographies.includes(geo))
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((resource) =>
        resource.tags?.some((tag) => selectedTags.includes(tag))
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (resource) =>
          resource.title?.toLowerCase().includes(query) ||
          resource.description?.toLowerCase().includes(query) ||
          resource.author?.toLowerCase().includes(query)
      );
    }

    setFilteredResources(filtered);
  }, [
    selectedPrograms,
    selectedSchools,
    selectedGeographies,
    selectedTags,
    searchQuery,
    resources,
  ]);

  const toggleFilter = (filterArray, setFilterArray, value) => {
    if (filterArray.includes(value)) {
      setFilterArray(filterArray.filter((item) => item !== value));
    } else {
      setFilterArray([...filterArray, value]);
    }
  };

  const clearAllFilters = () => {
    setSelectedPrograms([]);
    setSelectedSchools([]);
    setSelectedGeographies([]);
    setSelectedTags([]);
    setSearchQuery('');
  };

  const handleResourceClick = (resource) => {
    // Navigate to the resource detail page using hashid
    const hashId = encodeHashId(resource.id);
    navigate(`/resources/${hashId}`);
  };

  // Generate Table of Contents from HTML content (only H2 titles)
  const generateTableOfContents = (htmlContent) => {
    if (!htmlContent) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const headings = doc.querySelectorAll('h2'); // Only H2 titles

    const toc = [];
    headings.forEach((heading, index) => {
      const text = heading.textContent;
      const id = `heading-${index}`;

      // Add ID to the heading for anchor linking
      heading.setAttribute('id', id);

      toc.push({
        id,
        text,
      });
    });

    // Return updated HTML with IDs and TOC array
    return {
      toc,
      updatedHtml: doc.body.innerHTML,
    };
  };

  // If viewing a specific resource
  if (selectedResource) {
    // Generate TOC from content
    const { toc, updatedHtml } = selectedResource.content
      ? generateTableOfContents(selectedResource.content)
      : { toc: [], updatedHtml: '' };

    return (
      <div className="resources-layout">
        <Sidebar />
        <div className="resources-content">
          <div className="resource-detail-container">
            <button
              className="btn-back-to-resources"
              onClick={() => {
                setSelectedResource(null);
                navigate('/resources');
              }}
            >
              <FiX /> Back to Resources
            </button>

            <div className="resource-detail-card">
              <div className="resource-detail-header">
                <h1>{selectedResource.title}</h1>
                {selectedResource.author && (
                  <div className="resource-author">
                    <FiUser /> By {selectedResource.author}
                  </div>
                )}
                {selectedResource.published_date && (
                  <div className="resource-date">
                    <FiCalendar />{' '}
                    {new Date(
                      selectedResource.published_date
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>

              <div className="resource-detail-body">
                {/* Table of Contents */}
                {toc.length > 0 && (
                  <div className="resource-toc">
                    <ul className="resource-toc-list">
                      {toc.map((item) => (
                        <li key={item.id} className="resource-toc-item">
                          <a
                            href={`#${item.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              document
                                .getElementById(item.id)
                                ?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Render HTML content */}
                {updatedHtml && (
                  <div
                    className="resource-html-content"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(updatedHtml),
                    }}
                  />
                )}

                {selectedResource.tags && selectedResource.tags.length > 0 && (
                  <div className="resource-tags-section">
                    <strong>Tags:</strong>
                    <div className="resource-tags-list">
                      {selectedResource.tags.map((tag, index) => (
                        <span key={index} className="resource-tag-badge">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedResource.programs &&
                  selectedResource.programs.length > 0 && (
                    <div className="resource-meta-section">
                      <strong>Programs:</strong>{' '}
                      {selectedResource.programs.join(', ')}
                    </div>
                  )}

                {selectedResource.schools &&
                  selectedResource.schools.length > 0 && (
                    <div className="resource-meta-section">
                      <strong>Schools:</strong>{' '}
                      {selectedResource.schools.join(', ')}
                    </div>
                  )}

                {selectedResource.geographies &&
                  selectedResource.geographies.length > 0 && (
                    <div className="resource-meta-section">
                      <strong>Geographies:</strong>{' '}
                      {selectedResource.geographies.join(', ')}
                    </div>
                  )}
              </div>

              {(selectedResource.external_url || selectedResource.file_url) && (
                <div className="resource-detail-actions">
                  {selectedResource.external_url && (
                    <a
                      href={selectedResource.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-resource-action btn-primary"
                    >
                      <FiExternalLink /> Open Resource
                    </a>
                  )}
                  {selectedResource.file_url && (
                    <a
                      href={selectedResource.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-resource-action btn-secondary"
                    >
                      <FiDownload /> Download
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="resources-layout">
      <Sidebar />
      <div className="resources-content">
        <div className="resources-header">
          <div className="resources-header-content">
            <div>
              <h1>Study Resources</h1>
              <p>
                Access curated resources for your educational journey - guides,
                templates, articles, and more
              </p>
            </div>
            {/* Language Toggle */}
            <div className="language-toggle">
              <FiGlobe className="language-icon" />
              <button
                className={`language-btn ${selectedLanguage === 'en' ? 'active' : ''}`}
                onClick={() => setSelectedLanguage('en')}
              >
                EN
              </button>
              <span className="language-divider">|</span>
              <button
                className={`language-btn ${selectedLanguage === 'fr' ? 'active' : ''}`}
                onClick={() => setSelectedLanguage('fr')}
              >
                FR
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="resources-search-bar">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search resources by title, description, or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button
              className="clear-search-btn"
              onClick={() => setSearchQuery('')}
            >
              <FiX />
            </button>
          )}
        </div>

        {/* Filter Section */}
        <div className="resources-filter-section">
          <div className="resources-filter-header">
            <h3>
              <FiFilter /> Filter Resources
            </h3>
            <p className="resources-filter-subtitle">
              Find resources tailored to your needs
            </p>
            {(selectedPrograms.length > 0 ||
              selectedSchools.length > 0 ||
              selectedGeographies.length > 0 ||
              selectedTags.length > 0) && (
              <button className="clear-filters-btn" onClick={clearAllFilters}>
                <FiX /> Clear All
              </button>
            )}
          </div>

          {/* Programs Filter */}
          {availablePrograms.length > 0 && (
            <div className="filter-group">
              <h4>
                <FiBookOpen /> Programs
              </h4>
              <div className="filter-tags-container">
                {availablePrograms.map((program) => (
                  <button
                    key={program}
                    className={`filter-tag ${selectedPrograms.includes(program) ? 'active' : ''}`}
                    onClick={() =>
                      toggleFilter(
                        selectedPrograms,
                        setSelectedPrograms,
                        program
                      )
                    }
                  >
                    {program}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Schools Filter */}
          {availableSchools.length > 0 && (
            <div className="filter-group">
              <h4>
                <FiFileText /> Schools
              </h4>
              <div className="filter-tags-container">
                {availableSchools.map((school) => (
                  <button
                    key={school}
                    className={`filter-tag ${selectedSchools.includes(school) ? 'active' : ''}`}
                    onClick={() =>
                      toggleFilter(selectedSchools, setSelectedSchools, school)
                    }
                  >
                    {school}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Geographies Filter */}
          {availableGeographies.length > 0 && (
            <div className="filter-group">
              <h4>
                <FiMapPin /> Geographies
              </h4>
              <div className="filter-tags-container">
                {availableGeographies.map((geo) => (
                  <button
                    key={geo}
                    className={`filter-tag ${selectedGeographies.includes(geo) ? 'active' : ''}`}
                    onClick={() =>
                      toggleFilter(
                        selectedGeographies,
                        setSelectedGeographies,
                        geo
                      )
                    }
                  >
                    {geo}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tags Filter */}
          {availableTags.length > 0 && (
            <div className="filter-group">
              <h4>
                <FiTag /> Tags
              </h4>
              <div className="filter-tags-container">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    className={`filter-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() =>
                      toggleFilter(selectedTags, setSelectedTags, tag)
                    }
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Resources Grid */}
        <div className="resources-results-section">
          <div className="resources-results-header">
            <h3>
              {filteredResources.length}{' '}
              {filteredResources.length === 1 ? 'Resource' : 'Resources'} Found
            </h3>
          </div>

          {isLoadingResources ? (
            <div className="resources-loading">
              <div className="spinner"></div>
              <p>Loading resources...</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="resources-empty-state">
              <FiFileText size={48} />
              <h3>No resources found</h3>
              <p>Try adjusting your filters or search query</p>
              {(selectedPrograms.length > 0 ||
                selectedSchools.length > 0 ||
                selectedGeographies.length > 0 ||
                selectedTags.length > 0 ||
                searchQuery) && (
                <button className="btn-clear-filters" onClick={clearAllFilters}>
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="resources-grid">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  className="resource-card"
                  onClick={() => handleResourceClick(resource)}
                >
                  <div className="resource-card-header">
                    <div className="resource-icon">
                      <FiFileText />
                    </div>
                    <h4>{resource.title}</h4>
                  </div>

                  <p className="resource-description">{resource.description}</p>

                  {resource.author && (
                    <div className="resource-author">
                      <FiUser size={14} /> {resource.author}
                    </div>
                  )}

                  {resource.tags && resource.tags.length > 0 && (
                    <div className="resource-tags">
                      {resource.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="resource-tag">
                          {tag}
                        </span>
                      ))}
                      {resource.tags.length > 3 && (
                        <span className="resource-tag-more">
                          +{resource.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="resource-card-footer">
                    {resource.external_url && (
                      <span className="resource-type">
                        <FiExternalLink size={14} /> External Link
                      </span>
                    )}
                    {resource.file_url && (
                      <span className="resource-type">
                        <FiDownload size={14} /> Download
                      </span>
                    )}
                  </div>

                  {/* Call to Action */}
                  <div className="resource-card-cta btn-lwz-secondary">
                    <FiBookOpen size={16} />
                    <span>Open & Read</span>
                    <FiArrowRight size={14} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toast Notification */}
        {toast.show && (
          <div
            className={`toast-notification ${toast.success ? 'success' : 'error'}`}
          >
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Resources;
